import checkmarkIcon from '@/assets/images/public-pages/trusted-partners-icon-checkmark.svg';
import { PUBLIC_BODY_CLASS } from '../public-text';
import { useIssueReportOffer } from '@/hooks/useIssueReportOffer';
import { fillOfferLine, fillOfferTokens, usesOfferTokens } from '@/lib/utils/offer-tokens.utils';
import { DEFAULT_COMPARISON_SPLIT_BACKGROUND } from '../default-images';
import { SectionCta, type SectionOfferCta } from '../SectionCta';
import type { SectionRendererProps } from '../section-renderer.types';

interface ComparisonCard {
  title: string;
  items?: string[];
  // Due Diligence Platform's closing comparison band (docs/specs/features/
  // public-pages/02-design.md §23, FR-157) - a prose line, a price recap, and
  // the card's own CTA, in place of a bullet list. Trusted Partners' usage
  // leaves these unset.
  description?: string;
  priceLine?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  // MYN-285 (FR-202): marks this card's CTA as the Issue Report buy action, so
  // it follows the public offer. `description`/`priceLine` may carry the
  // offer's `{{issueReportPrice}}` / `{{issueReportAccessLength}}` placeholders.
  // Unset on the Seed Financing card and on Trusted Partners' usage.
  offerCta?: SectionOfferCta;
  // MYN-301 (FR-217): opens this card's CTA in a new tab. Set on the Due
  // Diligence page's closing comparison band's rightCard ("Schedule a Package
  // Call") only; every other usage leaves it unset.
  ctaOpensInNewTab?: boolean;
}

interface ComparisonSplitContent {
  eyebrow?: string;
  title?: string;
  leftCard: ComparisonCard;
  rightCard: ComparisonCard;
}

/**
 * Section background, keyed by `section.key` - every other comparison-split
 * usage sits on plain white. Due Diligence Platform's closing
 * "choose-your-next-step" band (MYN-269, explicit instruction) matches
 * Benefits' own closing band ("dont-wait", a `cta-banner` with the `teal`
 * dark variant) - same `bg-teal` token, so the two pages' final bands share
 * one color theme. Card content/layout is untouched; only this section-level
 * wrapper and its eyebrow/title (below) change.
 */
const SECTION_BACKGROUND_CLASS: Partial<Record<string, string>> = {
  'choose-your-next-step': 'bg-teal',
};

/**
 * Eyebrow/title colors, keyed by `section.key` - paired with
 * `SECTION_BACKGROUND_CLASS` above so "choose-your-next-step" reads correctly
 * on its new dark teal background (yellow eyebrow, white title - the same
 * pairing `cta-banner`'s own teal/dark variants use).
 */
const HEADER_COLOR_OVERRIDE: Partial<Record<string, { eyebrow: string; title: string }>> = {
  'choose-your-next-step': { eyebrow: 'text-yellow', title: 'text-white' },
};

/**
 * Two side-by-side cards (Trusted Partners' "The Difference", FR-144,
 * `02-design.md` §22; extended for Due Diligence Platform's closing
 * comparison band, §23, FR-157). `leftCard`/`rightCard` are independently
 * labeled, each either a bullet list (Trusted Partners' contrasting
 * problem/solution panels) or a prose description + price + CTA (Due
 * Diligence Platform's two pricing options) - not a repeated `cardList` of N
 * identical items, which is what `card-grid` already covers.
 *
 * Renders over a full-bleed background photo (Figma's "Business Background
 * Image" node, found missing during manual review, 2026-10-08), faded to
 * white toward the bottom edge so the section settles back onto the page's
 * white background before the next section begins.
 */
export function ComparisonSplitRenderer({ section }: SectionRendererProps) {
  const c = section.content as unknown as ComparisonSplitContent;
  const backgroundImage = DEFAULT_COMPARISON_SPLIT_BACKGROUND[section.key];
  const sectionBackgroundClass = SECTION_BACKGROUND_CLASS[section.key] ?? 'bg-white';
  const headerColors = HEADER_COLOR_OVERRIDE[section.key];
  const eyebrowClass = headerColors?.eyebrow ?? 'text-dkgreen';
  const titleClass = headerColors?.title ?? 'text-teal';

  return (
    <section className={`relative w-full overflow-hidden ${sectionBackgroundClass}`}>
      {backgroundImage && (
        <>
          <img src={backgroundImage} alt="" className="absolute inset-0 size-full object-cover" />
          {/* Fades the background photo to white toward the bottom edge -
            only meaningful when there is a photo to fade; a plain colored
            section (e.g. choose-your-next-step's teal) has no photo and must
            keep its own flat color, not wash out to white. */}
          <div className="from-white/0 absolute inset-0 bg-gradient-to-b to-white" />
        </>
      )}
      <div className="relative mx-auto flex w-full max-w-328 flex-col items-center gap-12 px-4 py-16">
        {/* Figma's own eyebrow color is pink (#ef476f) - removed per MYN-215/A-125
          and replaced with the existing accent palette, not reproduced. */}
        <div className="flex flex-col items-center gap-2 text-center">
          {c.eyebrow && <p className={`text-p6 font-semibold uppercase ${eyebrowClass}`}>{c.eyebrow}</p>}
          {c.title && <h2 className={`text-h2 ${titleClass}`}>{c.title}</h2>}
        </div>
        <div className="grid w-full grid-cols-1 gap-8 lg:grid-cols-2">
          <ComparisonCardPanel card={c.leftCard} variant="problem" />
          <ComparisonCardPanel card={c.rightCard} variant="solution" />
        </div>
      </div>
    </section>
  );
}

function ComparisonCardPanel({ card, variant }: { card: ComparisonCard; variant: 'problem' | 'solution' }) {
  if (card.description !== undefined) {
    return <PricingCardPanel card={card} />;
  }

  const isSolution = variant === 'solution';
  return (
    <div className={`flex flex-col gap-6 rounded-2xl p-12 ${isSolution ? 'bg-dkgreen' : 'bg-navy'}`}>
      <p className="text-h3 text-white">{card.title}</p>
      <ul className="flex flex-col gap-5">
        {card.items?.map((item, i) => (
          <li key={i} className="flex items-start gap-3">
            <BulletMark solid={!isSolution} />
            <p className="text-p4 text-white">{item}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Due Diligence Platform's closing comparison band (§23, FR-157) - a prose
 * description, price recap, and the card's own CTA, rather than Trusted
 * Partners' dark problem/solution bullet panels (which don't fit two
 * positive pricing options). Uses the same white/bordered card treatment as
 * `card-grid`'s `GridCard`, matching the rest of the site's card look.
 */
function PricingCardPanel({ card }: { card: ComparisonCard }) {
  // Only a card that carries an offer placeholder or CTA asks for the offer, so
  // the Seed Financing card and Trusted Partners' panels never make the
  // request. Until it is known (or when it failed) the placeholders are
  // removed and the price line dropped (FR-202).
  const needsOffer = usesOfferTokens(card.description, card.priceLine);
  const { data: offer } = useIssueReportOffer({ enabled: needsOffer });
  const priceLine = fillOfferLine(card.priceLine, offer);
  // A description made only of placeholders fills to nothing: no empty paragraph.
  const description = card.description ? fillOfferTokens(card.description, offer) : '';

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-slate bg-white p-12">
      <p className="text-h3 text-navy">{card.title}</p>
      {description && <p className={PUBLIC_BODY_CLASS}>{description}</p>}
      {priceLine && <p className="text-p3 font-semibold text-navy">{priceLine}</p>}
      {card.ctaLabel && (
        <SectionCta
          label={card.ctaLabel}
          url={card.ctaUrl}
          offerCta={card.offerCta}
          openInNewTab={card.ctaOpensInNewTab}
        />
      )}
    </div>
  );
}

/**
 * A plain white dot for the "problem" card (Figma: a bare CSS circle, no
 * asset) vs. the real downloaded Figma checkmark icon for the "solution"
 * card - matches Figma's two distinct list-item treatments exactly.
 */
function BulletMark({ solid }: { solid: boolean }) {
  if (solid) {
    return <span className="mt-2 size-3.5 shrink-0 rounded-full bg-white" aria-hidden />;
  }
  return <img src={checkmarkIcon} alt="" className="mt-1 size-6 shrink-0" />;
}
